import { actionChannel, all, call, cancel, fork, put, select, spawn, take, takeEvery, takeLeading } from 'redux-saga/effects';
import { buffers } from 'redux-saga';
import { formControlActions } from '../formControlSlice';
import {
    notificationHandler as notificationsHandler,
    readFromObj,
    writeInObj,
    getFormattedErrorMessage,
    resolveVideoWallGateCameraId,
} from '../../../utils/utils';
import { tableControlActions } from 'store/tableControlSlice/tableControlSlice';
import {
    selectFormById,
    selectFormsSlice,
    selectRelayWarningRequiredByTransactionId,
    selectRelaysConfigByTransactionId,
    selectAutomaticPulseSubmitEnabledByTransactionId,
    selectAutomaticPulseSubmitEligibleByTransactionId,
    selectAutomaticPulseDiscardEligibleByTransactionId,
    selectLookupBlockingValidationsByTransactionId,
    selectRelaySubmitBlockedByTransactionId,
    selectRelayEnforcementByTransactionId,
    selectSubmitBlockDialogByTransactionId,
    selectFaceMatchSubmitStatusByTransactionId,
    getActivePulseRelaySlotIds,
} from '../formControlSelectors';
import { blocksSubmitOnFaceMatch } from 'utils/faceMatchEnforcement';
import { OCR_VALUE_SOURCE, buildOcrVerificationPayload, getOcrAutofillValues, normalizeOcrValue } from 'utils/ocrVerification';
import { selectOcrSubmitStateByTransactionId } from 'store/ocrVerificationSlice/ocrVerificationSelectors';
import { hasBlockingValidations, overrideCoversBlockingValidations } from 'utils/lookupBlocking';
import {
    SUBMIT_BLOCKER,
    buildBlockSubmissionOverrides,
    findBlockSubmissionOverride,
    getOverridableOcrRejection,
    withOcrServerRejection,
} from 'utils/submitBlocking';
import { transactionActions, getGroupKey, resolveTransactionId } from 'store/transactionsSlice/transactionsSlice';
import * as transactionSelectors from 'store/transactionsSlice/transactionsSelectors';
import { store } from 'store/store';
import { axiosAiq } from 'config';
import { snackbarActions } from 'store/snackbarsSlice/snackbarsSlice';
import { sendLocalStorageSignal } from 'utils/sessionStorageHandlers';
import { populateEmptyTransactionFormFields } from '../fieldControllers';
import { cleanInputValue, formatInputValue, validateInputValue } from '../fieldValdiation/utils';
import {
    closeTransactionPanel,
    getTransactionEncodedImages,
    handleDeskAssignedMessage,
    leaveDesk,
    updateTransaction,
} from 'store/transactionsSlice/transactionSagas/transactionsSagas';
import { createStickyLaneTransaction, getStickyLaneErrorMessage } from 'services/stickyLaneService';
import { postCameraManual } from 'services/cameraDetectionService';
import { onInitializeEditTransactionForm, onEditFormStart, onRemoveTransaction } from './editTransaction';
import { sharedWorkerActions } from 'workers/sharedWorker/utils/utils';
import { perf } from 'store/transactionsSlice/utils/performance';
import copyToForm from './CopyToForm';
import { autoPopulateTransactionLookupValue, populateInventoryDataAutomation } from 'store/formControlSlice/formControlSagas/formControlSagaHelper';
import { inventoryValidation } from 'services/inventoryServices';
import { startLocationLookupDataLoad } from './locationLookupData';
import { markInventoryValidationSkipped } from './inventoryValidationSplitView';
import { getInventoryValidationSignature } from 'utils/inventoryValidation';
import { selectFeatureFlagData } from 'store/featureFlag/featureFlagSelector';
import { selectReopenTransaction, selectTransactions } from 'store/transactionsSlice/transactionsSelectors';
import { faceMatchActions } from 'store/faceMatchSlice/faceMatchSlice';
import { AUTOMATION_SOURCE, EXTERNAL_ANNOUNCEMENT_SOURCE, PROPERTY_NOT_FOUND } from 'utils/constants';
import { FENIX_EXPORTED_TO_YMS_FIELD, resolveLookupValueFromDisplay } from 'utils/fenixYms';
import { preserveRetiredLookupValues } from 'utils/lookupHelper';
import { selectFenixExportStatusByTransactionId } from 'store/fenixYmsSlice/fenixYmsSelectors';
import { parseErrorMessageFromResponse, convertText } from '../../../utils/utils';
import { invalidateAppointmentCache } from 'services/cachedAppointmentService';
import { saveDiscardedForm, getDiscardedForm, clearDiscardedForm } from 'utils/discardedFormCache';
import { ACTIVITY_END_REASON } from 'utils/activityTracking/constants';
import { recordCompletedReviewsForClose } from 'store/activityTracking/activityTrackingSagas';

const maxSubmitDiscardAttempts = 3;

function* getTransactionViewMode() {
    const isSplitViewMode = yield select(transactionSelectors.selectTransactionsSplitViewMode);
    return isSplitViewMode ? '[SPLIT_VIEW]' : '[TAB_VIEW]';
}

export function* initializeNewForm(transactionId, initNewFormData) {
    try {
        yield put(formControlActions.initializeNewTransactionFormStart());
        console.log(`initializeNewForm(): transactionId=${transactionId}, initialization of new form starts`);
        const isGateTransaction = initNewFormData?.gateTransactionId > 0;
        const transactionType = isGateTransaction ? 'gate' : 'trucks';
        const response = yield axiosAiq.get(`/transactions/${transactionType}/${transactionId}`);
        console.log(`initializeNewForm(): transactionId=${transactionId} transaction data received. `);
        const { data: transactionData } = response;
        const inventoryMode = transactionData?.gate?.location?.inventoryMode;

        yield put(
            formControlActions.initializeNewTransactionFormSuccess({
                transactionData,
            })
        );

        // The core form only needs transactionData + form config to render, so await that.
        yield call(fetchTransactionFormConfig, { transactionId, initNewFormData, inventoryMode });
        // Encoded images are only consumed by the media/camera panels (not the core form) and can be
        // a large payload (up to 20 images at 1-2MB each). Load them in the background via `spawn`
        // (detached) so the large image payload no longer gates transaction-open. The media panels
        // already render an empty-then-populated image list reactively, and `setEncodedImages` no-ops
        // if the form was removed before the images arrive.
        yield spawn(getTransactionEncodedImages, transactionId);
        console.log(`initializeNewForm(): Initialization of new form finished, transactionId=${transactionId}`);
    } catch (err) {
        yield call(notificationsHandler, {
            err,
            title: 'Error initializing new transaction form',
        });
        yield put(formControlActions.initializeNewTransactionFormFailed(err.message));
    }
}

export function prepareTransactionForSubmit(form) {
    const {
        form: { values },
        formConfig,
        transactionData,
        transactionIdAnnouncementUsed,
        internalIdAnnouncementUsed,
        externalAnnouncements,
    } = form;

    // dataField -> field config, so we can resolve a strict-lookup display label back to its enum
    // value before sending. Enum dropdowns (YesNoUnknown/YesNoNA/etc.) render the UNKNOWN option with
    // the "/" display label (see messages.properties: `YesNoUnknown.UNKNOWN=/`, valueExpr="value",
    // displayExpr="label"). If that display label ever ends up as the stored field value (e.g.
    // trailer.dolly), the backend cannot deserialize "/" into the enum and returns 400 BAD_REQUEST on
    // both /validate and submit. Resolving through the field's lookup maps "/" -> UNKNOWN, "Yes" ->
    // YES, etc. Non-lookup / free-solo fields (and forms without a config) are left untouched.
    const fieldConfigByDataField = new Map((formConfig?.fields ?? []).map((field) => [field.dataField, field]));

    // Fields that expect numeric values (Long/Integer) - convert "/" or invalid values to null
    const numericIdFields = ['person.companyId', 'person.clientPersonnelId', 'cargo.reeferSetTemp', 'announcement.transactionIdUsed'];

    const transactionToSubmit = structuredClone(transactionData);
    for (let fieldId of Object.keys(values)) {
        let formFieldValue = values[fieldId];

        // Resolve a strict-lookup display label (e.g. the "/" UNKNOWN sentinel) to its enum value so
        // the backend can deserialize it. No-op for non-lookup / free-solo fields and already-resolved
        // enum values.
        const fieldLookup = fieldConfigByDataField.get(fieldId)?.lookup;
        if (fieldLookup) {
            formFieldValue = resolveLookupValueFromDisplay(formFieldValue, fieldLookup);
        }

        // For numeric ID fields, convert "/" or invalid values to null
        if (numericIdFields.includes(fieldId)) {
            if (
                formFieldValue === '/' ||
                formFieldValue === '' ||
                (formFieldValue !== null && formFieldValue !== undefined && typeof formFieldValue !== 'number' && isNaN(Number(formFieldValue)))
            ) {
                formFieldValue = null;
            }
        }

        if (fieldId === FENIX_EXPORTED_TO_YMS_FIELD && (formFieldValue === '' || formFieldValue === '/')) {
            formFieldValue = null;
        }

        if (formFieldValue === '' || formFieldValue === null) {
            const receivedFieldValue = readFromObj(transactionToSubmit, fieldId);
            if (!receivedFieldValue) continue;
            // A cleared field must be sent as null, never an empty string. The backend cannot
            // coerce "" into typed values (enums like YesNoUnknown/YesNoNA, numbers, etc.), which
            // returns 400 BAD_REQUEST on both /validate and the submit endpoints (e.g. clearing
            // cargo.fourKitesStarted after the Truck+Loaded revert). null unambiguously means
            // "cleared" for every field type.
            formFieldValue = null;
        }

        writeInObj(transactionToSubmit, fieldId, formFieldValue);
    }

    // Guard person.companyId on EVERY server-bound payload (this runs for submit, /validate, inventory
    // validation and return-to-queue). It must be a real organizationId from the company lookup. A
    // numeric company NAME (e.g. "205519" OCR'd/copied into the free-solo companyName field) is numeric
    // and slips through the isNaN guard above; it can also arrive verbatim from transactionData (e.g. a
    // reopened/already-poisoned transaction) without ever passing through the values loop. Null it
    // unless it matches a lookup organizationId. (submitForm additionally reconciles via
    // autoPopulateTransactionLookupValue, which can still resolve a valid id from the company name.)
    const companyOwnerField = (formConfig?.fields ?? []).find((field) => field.referenceField === 'person.companyId');
    const companyLookup = companyOwnerField?.lookup;
    if (companyLookup?.referenceExpr && Array.isArray(companyLookup.dataSource) && companyLookup.dataSource.length) {
        const currentCompanyId = readFromObj(transactionToSubmit, 'person.companyId');
        if (currentCompanyId !== null && currentCompanyId !== undefined && currentCompanyId !== '' && currentCompanyId !== PROPERTY_NOT_FOUND) {
            const isKnownCompanyId = companyLookup.dataSource.some(
                (company) => String(company?.[companyLookup.referenceExpr]) === String(currentCompanyId)
            );
            if (!isKnownCompanyId) writeInObj(transactionToSubmit, 'person.companyId', null);
        }
    }

    transactionIdAnnouncementUsed && (transactionToSubmit.announcement.transactionIdUsed = transactionIdAnnouncementUsed);
    internalIdAnnouncementUsed && (transactionToSubmit.internalIdAnnouncementUsed = internalIdAnnouncementUsed);
    const bisonAnnouncements = externalAnnouncements?.[EXTERNAL_ANNOUNCEMENT_SOURCE.BISON];
    if (bisonAnnouncements?.length) transactionToSubmit.externalAnnouncements = bisonAnnouncements;
    return transactionToSubmit;
}

// Returns the name of the first field that currently has a truthy validation error, or undefined.
export function getFirstInvalidField(form) {
    const errors = form?.form?.errors ?? {};
    return Object.keys(errors).find((fieldName) => errors[fieldName]);
}

// Pure decision for Quick Submit + Pulse (Ctrl+Shift+Enter), kept separate from submitForm's yield
// effects so it's directly unit-testable without stepping the generator. A lane that doesn't
// support auto-pulse on submit at all (automaticPulse 'NONE') proceeds silently - no pulse, no
// warning.
export function resolveSubmitPulseOutcome({ automaticPulseSubmitEnabled, pulseRelaySlotIds }) {
    if (!automaticPulseSubmitEnabled) return { pulse: false, warn: false };
    return pulseRelaySlotIds.length > 0 ? { pulse: true, warn: false } : { pulse: false, warn: true };
}

// Truck transactions only: `/transactions/gate` (dynamic forms) neither checks nor stores OCR verification.
export function* isOcrSubmitBlocked({ transactionId, blockSubmissionOverrides = null, dynamicForm = null }) {
    if (dynamicForm || findBlockSubmissionOverride(blockSubmissionOverrides, SUBMIT_BLOCKER.OCR_VERIFICATION)) return false;
    const { issues } = yield select(selectOcrSubmitStateByTransactionId(transactionId));
    return issues.length > 0;
}

// Sets Trailer # / Seal # to the verified vehicle value where the field is empty or holds an ALISA or earlier OCR
// value, on Submit and Ctrl+Enter only. Runs after the quick fill, so it sees the final Cargo Status and replaces a
// "/" or ALISA filler, and before the required-field check. Returns whether a field was set.
export function* applyOcrAutofill(transactionId) {
    const { required, verifications, fieldValues, fieldValueSources } = yield select(selectOcrSubmitStateByTransactionId(transactionId));
    if (!required) return false;
    const autofill = Object.entries(getOcrAutofillValues(verifications, fieldValues, fieldValueSources));
    if (!autofill.length) return false;

    // Cleaned, formatted and checked like a quick fill value: a number that breaks the field's pattern stops the
    // submit at the required-field check. The BE compares the submitted number with the verified one after OCR
    // normalization, so a format that would change it (a dash, a prefix) is not applied.
    const fields = (yield select(selectFormById(transactionId)))?.formConfig?.fields ?? [];
    const entries = autofill.map(([fieldName, { value, source }]) => {
        const { format, validationRules } = fields.find(({ dataField }) => dataField === fieldName) ?? {};
        const formattedValue = formatInputValue(cleanInputValue(value), format);
        const filledValue = normalizeOcrValue(formattedValue) === normalizeOcrValue(value) ? formattedValue : value;
        return {
            fieldName,
            value: filledValue,
            source: source === OCR_VALUE_SOURCE.CONDOR ? AUTOMATION_SOURCE.CONDOR : AUTOMATION_SOURCE.AI_OCR,
            error: validateInputValue(filledValue, validationRules),
        };
    });

    yield put(
        formControlActions.applyAutomationToFieldValues({
            formId: transactionId,
            automationEntries: entries.map(({ fieldName, value, source }) => ({ fieldName, value, source })),
        })
    );
    for (const { fieldName, error } of entries) {
        if (error) yield put(formControlActions.setFieldError({ formId: transactionId, fieldName, error }));
    }
    console.log(`applyOcrAutofill(): transactionId=${transactionId} filled ${entries.map(({ fieldName }) => fieldName).join(', ')}`);
    return true;
}

export function* isFaceMatchSubmitBlocked({ transactionId, blockSubmissionOverrides = null }) {
    if (findBlockSubmissionOverride(blockSubmissionOverrides, SUBMIT_BLOCKER.FACE_MATCH)) return false;
    return blocksSubmitOnFaceMatch(yield select(selectFaceMatchSubmitStatusByTransactionId(transactionId)));
}

export function* isLookupSubmitBlocked({ transactionId, blockSubmissionOverrides = null }) {
    const blockingValidations = yield select(selectLookupBlockingValidationsByTransactionId(transactionId));
    if (!hasBlockingValidations(blockingValidations)) return false;
    const lookupOverride = findBlockSubmissionOverride(blockSubmissionOverrides, SUBMIT_BLOCKER.LOOKUP);
    return !overrideCoversBlockingValidations(lookupOverride, blockingValidations);
}

export function resolveRelaySubmitBlock({ relaySubmitBlocked, pulseShortcutUsed = false, automaticPulseSubmitEligible = false }) {
    if (!relaySubmitBlocked) return false;
    return !(pulseShortcutUsed && automaticPulseSubmitEligible);
}

export function* isRelaySubmitBlocked({ transactionId, pulseShortcutUsed = false, blockSubmissionOverrides = null }) {
    if (findBlockSubmissionOverride(blockSubmissionOverrides, SUBMIT_BLOCKER.RELAY)) return false;

    const relaySubmitBlocked = yield select(selectRelaySubmitBlockedByTransactionId(transactionId));
    if (!relaySubmitBlocked) return false;

    let automaticPulseSubmitEligible = false;
    if (pulseShortcutUsed) {
        automaticPulseSubmitEligible = yield select(selectAutomaticPulseSubmitEligibleByTransactionId(transactionId));
    }

    return resolveRelaySubmitBlock({ relaySubmitBlocked, pulseShortcutUsed, automaticPulseSubmitEligible });
}

// Collects every blocker at once, so the agent answers all of them in a single dialog. The overrides
// ride in the submit payload only, which is what re-arms every blocker after a failed submit.
export function* resolveSubmitBlockers({ transactionId, pulseShortcutUsed = false, blockSubmissionOverrides = null, dynamicForm }) {
    const blockers = [];
    if (yield call(isOcrSubmitBlocked, { transactionId, blockSubmissionOverrides, dynamicForm })) blockers.push(SUBMIT_BLOCKER.OCR_VERIFICATION);
    if (yield call(isFaceMatchSubmitBlocked, { transactionId, blockSubmissionOverrides })) blockers.push(SUBMIT_BLOCKER.FACE_MATCH);
    if (yield call(isLookupSubmitBlocked, { transactionId, blockSubmissionOverrides })) blockers.push(SUBMIT_BLOCKER.LOOKUP);
    if (yield call(isRelaySubmitBlocked, { transactionId, pulseShortcutUsed, blockSubmissionOverrides })) blockers.push(SUBMIT_BLOCKER.RELAY);
    return blockers;
}

export function* overrideSubmitBlock({ payload: { transactionId, reasons } }) {
    const parkedDialog = yield select(selectSubmitBlockDialogByTransactionId(transactionId));
    const blockingValidations = yield select(selectLookupBlockingValidationsByTransactionId(transactionId));
    const relaySubmitBlocked = yield select(selectRelaySubmitBlockedByTransactionId(transactionId));
    const relayEnforcement = yield select(selectRelayEnforcementByTransactionId(transactionId));
    const faceMatchStatus = yield select(selectFaceMatchSubmitStatusByTransactionId(transactionId));
    const ocrSubmitState = withOcrServerRejection(yield select(selectOcrSubmitStateByTransactionId(transactionId)), parkedDialog?.ocrServerRejection);

    yield put(formControlActions.closeSubmitBlockDialog({ transactionId }));

    if (!parkedDialog?.payload) {
        console.warn(`overrideSubmitBlock(): transactionId=${transactionId} overridden with no parked submit to resume`);
        return;
    }

    // Built from LIVE state, not from what was blocking when the dialog opened.
    const blockSubmissionOverrides = buildBlockSubmissionOverrides({
        carried: parkedDialog.payload.blockSubmissionOverrides,
        blockers: parkedDialog.blockers,
        reasons,
        liveState: { blockingValidations, relaySubmitBlocked, relayEnforcement, faceMatchStatus, ocrSubmitState },
    });

    // eslint-disable-next-line no-unused-vars
    const { blockSubmissionOverrides: carried, ...parkedPayload } = parkedDialog.payload;
    const resumeSubmit = parkedDialog.startNew ? formControlActions.submitFormAndStartNewStart : formControlActions.submitFormStart;

    yield put(resumeSubmit(blockSubmissionOverrides.length ? { ...parkedPayload, blockSubmissionOverrides } : parkedPayload));
}

// The gate mode as the BE has it now, into the open form: an admin can turn OCR on (or add the seal) while the
// transaction is open. Best effort: without it the dialog still opens, on the BE message.
export function* refreshGateOcrVerification(transactionId) {
    try {
        const { data } = yield call([axiosAiq, axiosAiq.get], `/transactions/trucks/${transactionId}`);
        const ocrVerification = data?.gate?.ocrVerification;
        const form = yield select(selectFormById(transactionId));
        if (!ocrVerification || !form?.transactionData?.gate) return;
        const { transactionData } = form;
        yield put(
            formControlActions.setTransactionData({ transactionData: { ...transactionData, gate: { ...transactionData.gate, ocrVerification } } })
        );
    } catch (err) {
        console.warn(`refreshGateOcrVerification(): transactionId=${transactionId} - the gate could not be read again`, err);
    }
}

// The BE refused the OCR verification of a submit: the gate turned OCR on after the transaction was opened, so the
// form sent no result or a partial one. With the gate mode read again the standard OCR dialog opens, as if OCR had
// been on from the start: no toast, and the BE message only in the override's snapshot (and on screen when the form
// still sees nothing to fix). Resumed as a plain submit, since a Submit and start new already started the next one.
export function* openOcrRejectionDialog({ transactionId, payload, message }) {
    console.log(`submitForm(): transactionId=${transactionId} OCR verification rejected by the BE: ${message}`);
    yield put(formControlActions.setSubmitFormInProgress({ formId: transactionId, inProgress: false }));
    yield call(refreshGateOcrVerification, transactionId);
    yield put(
        formControlActions.openSubmitBlockDialog({
            transactionId,
            startNew: false,
            blockers: [SUBMIT_BLOCKER.OCR_VERIFICATION],
            payload: { ...payload, deferClose: false },
            ocrServerRejection: message,
        })
    );
}

export function* submitForm({ payload }) {
    const {
        transactionId,
        locationId,
        tabId,
        dynamicForm,
        dynamicFormData,
        autoPopulateEmptyFormFields,
        deferClose = false,
        retryAttempt = 0,
        pulseShortcutUsed = false,
        blockSubmissionOverrides = null,
    } = payload;
    let form = yield select(selectFormById(transactionId));
    try {
        yield put(formControlActions.setSubmitFormInProgress({ formId: transactionId }));
        const { reopenStatus = null } = yield select(selectReopenTransaction);
        const allTransactions = yield select(selectTransactions);
        const isTransactionReopened = !!allTransactions.find((transaction) => transaction?.id === transactionId && transaction?.reopened);

        autoPopulateEmptyFormFields && (form = (yield call(populateEmptyTransactionFormFields, form)) || form);
        if (!dynamicForm && (yield call(applyOcrAutofill, transactionId))) form = yield select(selectFormById(transactionId));

        const errors = form?.form?.errors;

        for (let fieldName of Object.keys(errors)) {
            if (errors[fieldName]) {
                yield put(
                    formControlActions.setFieldTouched({
                        formId: transactionId,
                        fieldName,
                    })
                );
            }
        }

        const firstInvalidField = getFirstInvalidField(form);
        if (firstInvalidField) {
            yield put(formControlActions.setSubmitFormInProgress({ formId: transactionId, inProgress: false }));
            yield call(notificationsHandler, {
                title: 'Fill all required fields!',
                variant: 'warning',
            });

            const form = document.getElementById(transactionId);
            const element = form ? form.querySelector(`[name="${firstInvalidField}"]`) : document.querySelector(`[name="${firstInvalidField}"]`);
            if (!element) {
                console.warn(`submitForm(): transactionId=${transactionId}, element with name="${firstInvalidField}" not found in the DOM.`);
                return false;
            }
            element.scrollIntoView({
                behavior: 'smooth',
                block: 'end',
            });
            if (typeof element.select === 'function') {
                element.select();
            } else {
                console.warn(
                    `submitForm(): transactionId=${transactionId}, element with name="${firstInvalidField}" has no .select() method.`,
                    element,
                    errors
                );
            }
            return false;
        }

        const submitBlockers = yield call(resolveSubmitBlockers, { transactionId, pulseShortcutUsed, blockSubmissionOverrides, dynamicForm });
        if (submitBlockers.length) {
            yield put(formControlActions.setSubmitFormInProgress({ formId: transactionId, inProgress: false }));
            yield put(formControlActions.openSubmitBlockDialog({ transactionId, startNew: !!deferClose, blockers: submitBlockers, payload }));
            console.log(`submitForm(): transactionId=${transactionId} submit blocked by ${submitBlockers.join(', ')}`);
            return false;
        }

        console.log(`submitForm(): transactionId=${transactionId} initiating submit`);
        let filledTransactionToSubmit;
        if (!dynamicForm) {
            const transactionToSubmit = yield call(prepareTransactionForSubmit, form);
            filledTransactionToSubmit = yield call(autoPopulateTransactionLookupValue, form, transactionToSubmit);
        } else {
            filledTransactionToSubmit = structuredClone(form.transactionData);
            filledTransactionToSubmit.form = dynamicForm;
            filledTransactionToSubmit.values = dynamicFormData;
        }

        filledTransactionToSubmit.agentTime = form?.form?.values?.agentTime ?? null;
        filledTransactionToSubmit.agentAudioTime = form?.form?.values?.agentAudioTime ?? form?.transactionData?.agentAudioTime ?? null;

        filledTransactionToSubmit.ymsExportStatus = yield select(selectFenixExportStatusByTransactionId(transactionId));

        // Not in prepareTransactionForSubmit: that builder is shared with the per-field validate,
        // inventory validation and return-to-queue, which must never carry the override.
        if (blockSubmissionOverrides?.length) filledTransactionToSubmit.blockSubmissionOverrides = blockSubmissionOverrides;

        // Sent whenever OCR is mandatory, also with an OCR override: the BE stores it, and checks it without one.
        if (!dynamicForm) {
            const ocrSubmitState = yield select(selectOcrSubmitStateByTransactionId(transactionId));
            if (ocrSubmitState.required) filledTransactionToSubmit.ocrVerification = buildOcrVerificationPayload(ocrSubmitState.verifications);
        }

        if (isTransactionReopened) {
            filledTransactionToSubmit = {
                ...filledTransactionToSubmit,
                reopenStatus,
            };
        }

        const config = {
            headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json, application/javascript, text/javascript, text/json',
            },
        };

        const url = dynamicForm ? `/transactions/gate/${transactionId}` : `/transactions/trucks/${transactionId}`;
        yield axiosAiq.put(url, filledTransactionToSubmit, config);

        yield put(formControlActions.setSubmitFormInProgress({ formId: transactionId, inProgress: false }));

        yield sendLocalStorageSignal('transactionClosed', transactionId);
        sharedWorkerActions.transactionFinished(transactionId);

        // Invalidate all cached data for expanded panel topics (appointments, history, manual)
        invalidateAppointmentCache();
        yield put(tableControlActions.invalidateLocationTables(locationId));

        yield put(
            tableControlActions.fetchTableDataStart({
                tableId: locationId,
                locationId,
                pageNum: 0,
                fetchLocationAnnouncements: true,
            })
        );
        yield call(notificationsHandler, {
            title: `Transaction ${transactionId} successfully submited.`,
            variant: 'success',
        });

        // Quick Submit + Pulse: the decision (pulse vs. warn vs. silent no-op, resolveSubmitPulseOutcome
        // above) is made here, only once the submit above has actually succeeded, and reuses the exact
        // same triggers every manual relay click uses (formControlActions.activateRelay + setRelayUsed)
        // so relayUsed correctly reflects an automatic pulse too. This must run before the relayWarningRequired
        // check below, since setRelayUsed here is what the warning's "was a relay used" check reads.
        if (pulseShortcutUsed) {
            const automaticPulseSubmitEnabled = yield select(selectAutomaticPulseSubmitEnabledByTransactionId(transactionId));
            const relaysConfig = yield select(selectRelaysConfigByTransactionId(transactionId));
            const pulseRelaySlotIds = getActivePulseRelaySlotIds(relaysConfig);
            const { pulse, warn } = resolveSubmitPulseOutcome({ automaticPulseSubmitEnabled, pulseRelaySlotIds });
            if (pulse) {
                for (const relaySlotId of pulseRelaySlotIds) {
                    yield put(formControlActions.activateRelay({ relaySlotId, operation: 'PULSE' }));
                }
                yield put(formControlActions.setRelayUsed({ transactionId, relayUsed: true }));
            } else if (warn) {
                // Enabled but no Pulse relay configured: submit still succeeded, just inform the
                // agent that no relay fired.
                yield put(
                    snackbarActions.enqueueSnackbar({
                        message: 'No Pulse relay configured for this lane. Continuing without a Pulse.',
                        options: { variant: 'warning' },
                    })
                );
            }
        }

        const relayWarningRequired = yield select(selectRelayWarningRequiredByTransactionId(transactionId));
        if (relayWarningRequired) {
            yield put(
                snackbarActions.enqueueSnackbar({
                    message: 'Relays not used. Click Reopen if you want to use relays on the last transaction.',
                    options: {
                        variant: 'warning',
                    },
                })
            );
        }

        // When deferClose is set (sticky lane "start new"), the caller removes the panel after the
        // replacement transaction has rendered, so the old one stays visible in the meantime.
        if (!deferClose) {
            yield put(
                transactionActions.closeTransactionPanel({
                    tabId,
                    transactionId,
                    locationId,
                    // Reached only after a SUCCESSFUL submit response, which is what makes
                    // `completed_by_self` correct here - the in-progress flag fires before the
                    // request resolves and would stamp it on transactions that then failed.
                    activityEndReason: ACTIVITY_END_REASON.COMPLETED_BY_SELF,
                    activityReviewCompleted: true,
                })
            );
        }
        yield put(faceMatchActions.clearHistory({ transactionId }));
        // A submit persists the values on the transaction itself, so any cached
        // discarded-form snapshot for this transaction is now obsolete.
        clearDiscardedForm(transactionId);
        const viewModeTag = yield call(getTransactionViewMode);
        console.log(`submitForm(): ${viewModeTag} transactionId=${transactionId} is successfully submited`);
        yield put(transactionActions.setLastClosedTransactionId(transactionId));
        return true;
    } catch (err) {
        if (yield call(handleDeskInactive, err)) return false;
        if (err?.response?.status === 409) {
            yield call(updateTransaction, transactionId);
            if (retryAttempt < maxSubmitDiscardAttempts) {
                console.log(
                    `submitForm(): Mismatch version detected, transactionId=${transactionId} updated to newest version, retrying...  attempt #${
                        retryAttempt + 1
                    } of ${maxSubmitDiscardAttempts}`
                );
                return yield call(submitForm, { payload: { ...payload, retryAttempt: retryAttempt + 1 } });
            } else {
                yield put(formControlActions.setSubmitFormInProgress({ formId: transactionId, inProgress: false }));
                yield call(notificationsHandler, {
                    title: `New data was received for transaction ${transactionId}, if you want to save please click Submit again`,
                    variant: 'info',
                });
            }
            return false;
        }

        const ocrRejection = dynamicForm ? null : getOverridableOcrRejection(err);
        if (ocrRejection) {
            yield call(openOcrRejectionDialog, { transactionId, payload, message: ocrRejection });
            return false;
        }

        if (err?.response?.status === 400) {
            const { transactionData: { desk, gate, transactionSource = '' } = {}, form: formState = {} } = form || {};
            const formValues = formState?.values || {};
            const deskName = desk?.name;
            const lane = [gate?.location?.fullName, `${gate?.name}-${gate?.direction}`].filter(Boolean).join(' | ');
            const errorDetails = err?.response?.data?.details || [];
            const errorFields = errorDetails.map((detail) => detail?.field).filter(Boolean);

            console.log(
                `[SUBMIT_TRANSACTION] [BAD_REQUEST] transactionId=${transactionId}, deskName=${deskName}, lane=${lane}, transactionSource=${transactionSource}, errorFields=${errorFields.join(', ')}`
            );

            yield put(formControlActions.setSubmitFormInProgress({ formId: transactionId, inProgress: false }));

            const processedFields = [];

            for (const errorField of errorFields) {
                const rejectedValue = errorField?.rejectedValue ?? 'N/A';

                if (!errorField || !(errorField in formValues)) {
                    console.log(
                        `[SUBMIT_TRANSACTION] [BAD_REQUEST] [FIELD_MISMATCH] transactionId=${transactionId}, field="${errorField}", value="${rejectedValue}"`
                    );
                    continue;
                }

                processedFields.push(errorField);

                yield put(
                    formControlActions.setFieldValue({
                        formId: transactionId,
                        fieldName: errorField,
                        value: '',
                    })
                );
                yield put(
                    formControlActions.setFieldError({
                        formId: transactionId,
                        fieldName: errorField,
                        error: true,
                        helperText: 'Please manually enter a valid value',
                    })
                );
                yield put(
                    formControlActions.setFieldTouched({
                        formId: transactionId,
                        fieldName: errorField,
                    })
                );
            }

            const notificationMessage =
                processedFields.length > 0
                    ? `[BAD_REQUEST] Error submitting transaction. ${processedFields.length} field(s) need manual correction.`
                    : '[BAD_REQUEST] Error submitting transaction. Please check your data and try again.';

            yield call(notificationsHandler, {
                title: notificationMessage,
                variant: 'error',
            });

            if (processedFields.length > 0) {
                const firstErrorField = processedFields[0];
                const formElement = document.getElementById(transactionId);
                const element = formElement?.querySelector?.(`[name="${firstErrorField}"]`) ?? document.querySelector(`[name="${firstErrorField}"]`);

                if (element) {
                    element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    if (typeof element.focus === 'function') element.focus();
                } else {
                    console.warn(`[SUBMIT_TRANSACTION] [BAD_REQUEST] DOM element for field "${firstErrorField}" not found, cannot scroll to it`);
                }
            }

            return false;
        }

        if (err?.response?.status === 503) {
            yield put(formControlActions.setSubmitFormInProgress({ formId: transactionId, inProgress: false }));
            yield call(notificationsHandler, {
                title: 'AIQ not available right now, please try again later.',
                variant: 'info',
            });
            return false;
        }

        console.log(`submitForm(): transactionId=${transactionId}, transaction submitting error= ${JSON.stringify(err)}`);
        yield put(formControlActions.setSubmitFormInProgress({ formId: transactionId, inProgress: false }));

        yield call(notificationsHandler, {
            err,
            title: `Error submiting transaction ${transactionId}`,
        });
        return false;
    }
}

// Pure decision for Quick Discard + Pulse (Ctrl+Shift+Delete), kept separate from
// discardTransaction's yield effects so it's directly unit-testable without stepping the
// generator. Unlike submit, discard has no partially-enabled middle state - automaticPulse must
// be 'ALL' AND a relay configured (automaticPulseDiscardEligible) or the shortcut warns instead.
// This exact decision regressed once already (BEAST-12796) by gating the warning behind
// automaticPulseDiscardEnabled, which silently dropped it for NONE/SUBMIT lanes.
export function resolveDiscardPulseOutcome({ automaticPulseDiscardEligible }) {
    return automaticPulseDiscardEligible ? { pulse: true, warn: false } : { pulse: false, warn: true };
}

export function* discardTransaction({ payload }) {
    const {
        tabId,
        transactionId,
        locationId,
        retryAttempt = 0,
        dynamicForm,
        reopened,
        uuid,
        discardReason,
        deferClose = false,
        pulseShortcutUsed = false,
    } = payload;

    yield put(formControlActions.setdiscardTransactionInProgress({ transactionId }));
    let isTransactionReopened, reopenStatus;

    if (reopened) {
        const reopenedTransaction = yield select(selectReopenTransaction);
        isTransactionReopened = reopened && reopenedTransaction?.externalUuid === uuid; //using uuid comparison to accomodate dynamicForms also
        reopenStatus = (isTransactionReopened && reopenedTransaction?.reopenStatus) || null;
    }

    try {
        console.log(`discardTransaction(): transactionId=${transactionId} initiating discard`);

        const basePath = `/transactions/${dynamicForm ? 'gate' : 'trucks'}`;

        const form = yield select(selectFormById(transactionId));
        const agentTime = form?.form?.values?.agentTime ?? form?.transactionData?.agentTime ?? null;

        yield axiosAiq.put(`${basePath}/${transactionId}/discard`, { reopenStatus, discardReason, agentTime });

        // Discard only persists { reopenStatus, discardReason, agentTime } server-side,
        // so the operator's manually-edited field values would otherwise be lost on
        // reopen. Snapshot the edited editable fields locally so they can be
        // rehydrated if this transaction is reopened. Only snapshot AFTER the discard
        // request succeeds, so we never persist a snapshot for a transaction that was
        // not actually discarded. (Submit persists values on the transaction itself,
        // so it doesn't need this.)
        yield call(snapshotDiscardedFormValues, transactionId, form);

        yield sendLocalStorageSignal('transactionClosed', transactionId);

        sharedWorkerActions.transactionFinished(transactionId);

        // Invalidate all cached data for expanded panel topics (appointments, history, manual)
        invalidateAppointmentCache();
        yield put(tableControlActions.invalidateLocationTables(locationId));

        yield put(
            tableControlActions.fetchTableDataStart({
                tableId: locationId,
                locationId,
                pageNum: 0,
            })
        );
        // When deferClose is set (sticky lane "start new"), the caller removes the panel after the
        // replacement transaction has rendered, so the old one stays visible in the meantime.
        if (!deferClose) {
            yield put(
                transactionActions.closeTransactionPanel({
                    tabId,
                    transactionId,
                    locationId,
                    // Post-success discard / return to queue: the agent finished with it.
                    activityEndReason: ACTIVITY_END_REASON.COMPLETED_BY_SELF,
                })
            );
        }
        yield put(faceMatchActions.clearHistory({ transactionId }));
        yield put(transactionActions.setLastClosedTransactionId(transactionId));
        const viewModeTag = yield call(getTransactionViewMode);
        console.log(`discardTransaction(): ${viewModeTag} transactionId=${transactionId} is successfully discarded`);
        yield put(formControlActions.setdiscardTransactionInProgress({ transactionId, inProgress: false }));

        // Quick Discard + Pulse: the decision (pulse vs. warn, resolveDiscardPulseOutcome above) is
        // made here, only once the discard above has actually succeeded, and reuses the exact same
        // triggers every manual relay click uses (formControlActions.activateRelay + setRelayUsed).
        if (pulseShortcutUsed) {
            const automaticPulseDiscardEligible = yield select(selectAutomaticPulseDiscardEligibleByTransactionId(transactionId));
            const relaysConfig = yield select(selectRelaysConfigByTransactionId(transactionId));
            const pulseRelaySlotIds = getActivePulseRelaySlotIds(relaysConfig);
            const { pulse, warn } = resolveDiscardPulseOutcome({ automaticPulseDiscardEligible });
            if (pulse) {
                for (const relaySlotId of pulseRelaySlotIds) {
                    yield put(formControlActions.activateRelay({ relaySlotId, operation: 'PULSE' }));
                }
                yield put(formControlActions.setRelayUsed({ transactionId, relayUsed: true }));
            } else if (warn) {
                yield put(
                    snackbarActions.enqueueSnackbar({
                        message: 'No Pulse relay configured for this lane. Continuing without a Pulse.',
                        options: { variant: 'warning' },
                    })
                );
            }
        }
        return true;
    } catch (err) {
        if (yield call(handleDeskInactive, err)) return false;

        if (err.response?.status === 400) {
            const form = yield select(selectFormById(transactionId));
            if (!form) {
                yield call(notificationsHandler, {
                    err,
                    title: 'Error discarding transaction',
                });
                yield put(formControlActions.setdiscardTransactionInProgress({ transactionId, inProgress: false }));
                return false;
            }
            yield call(refreshTransaction, { transactionId, form });
            yield call(notificationsHandler, {
                variant: 'info',
                title: 'Transaction already FINISHED, data is autopopulated from previously submited transaction',
            });
            yield put(formControlActions.setdiscardTransactionInProgress({ transactionId, inProgress: false }));
            return false;
        }

        if (err?.response?.status === 409) {
            yield call(updateTransaction, transactionId);
            if (retryAttempt <= maxSubmitDiscardAttempts) {
                console.log(
                    `discardTransaction(): Mismatch version detected, transactionId=${transactionId} updated to newest version, retrying...  attempt #${
                        retryAttempt + 1
                    } of ${maxSubmitDiscardAttempts}`
                );
                return yield call(discardTransaction, { payload: { ...payload, retryAttempt: retryAttempt + 1 } });
            } else {
                yield call(notificationsHandler, {
                    title: 'New data was received for this transaction, if you want to discard please click Discard again',
                    variant: 'info',
                });
                yield put(formControlActions.setdiscardTransactionInProgress({ transactionId, inProgress: false }));
            }
            return false;
        }

        if (err?.response?.status === 503) {
            yield call(notificationsHandler, {
                title: 'AIQ not available right now, please try again later.',
                variant: 'info',
            });
            yield put(formControlActions.setdiscardTransactionInProgress({ transactionId, inProgress: false }));
            return false;
        }
        console.log(`discardTransaction(): transactionId=${transactionId}, discarding transaction error=${JSON.stringify(err)}`);

        yield call(notificationsHandler, {
            err,
            title: `Error discarding transaction ${transactionId}`,
        });
        yield put(formControlActions.setdiscardTransactionInProgress({ transactionId, inProgress: false }));
        return false;
    }
}

/**
 * Sticky Lane "Submit / Discard and start new".
 *
 * After a valid submit/discard on a pinned lane:
 *  - if other transactions remain for the same lane, activate the first of them;
 *  - otherwise ask the backend to mint a fresh (update-free) transaction for the gate, keep the
 *    original visible until the replacement has rendered, then remove the original and activate the
 *    replacement.
 *
 * The core submit/discard behavior is reused verbatim via `deferClose` so nothing on the normal path
 * changes.
 */

// Sets the given transaction as the active one, honoring split-view vs tab-view.
function* activateStartedTransaction(transaction) {
    if (!transaction) return;
    const isSplitViewMode = yield select(transactionSelectors.selectTransactionsSplitViewMode);
    if (isSplitViewMode) {
        yield put(transactionActions.setSplitViewActiveTransaction({ transactionId: transaction.id, transactionGroup: getGroupKey(transaction) }));
    } else {
        yield put(transactionActions.setActiveTab(transaction.tabId));
    }
}

// Activates the first remaining transaction that belongs to the given lane (gate).
function* activateFirstLaneTransaction(gateId) {
    const transactions = yield select(selectTransactions);
    const nextLaneTransaction = transactions.find((transaction) => transaction.gateId === gateId);
    yield call(activateStartedTransaction, nextLaneTransaction);
}

const maxCreateStickyLaneTransactionAttempts = 3;

// Business-rule errors (400) won't succeed on retry; transient ones (network / 409 / 5xx) will.
function isRetryableStickyLaneError(err) {
    const status = err?.response?.status;
    return !status || status === 409 || status === 503 || status >= 500;
}

// Mints a replacement transaction for the lane; returns the DeskAssignedMessage payload, or null on failure.
function* createLaneReplacementTransaction(gateId, attempt = 1) {
    try {
        return yield call(createStickyLaneTransaction, gateId);
    } catch (err) {
        if (isRetryableStickyLaneError(err) && attempt < maxCreateStickyLaneTransactionAttempts) {
            return yield call(createLaneReplacementTransaction, gateId, attempt + 1);
        }
        yield call(notificationsHandler, { title: getStickyLaneErrorMessage(err) });
        return null;
    }
}

// `call` (not `put`) so the close saga's own next-active selection completes before we activate below.
function* closeTransactionPanelAndWait({ transactionId, tabId, locationId, reviewCompleted = false }) {
    if (reviewCompleted) {
        yield call(recordCompletedReviewsForClose, { payload: { transactionId } });
    }
    yield put(transactionActions.cleanTransactionPanelOnClose(tabId));
    yield call(closeTransactionPanel, { payload: { tabId, transactionId, locationId } });
}

// Opens the replacement, removes the original, then activates the replacement.
function* finalizeStartNew({ payload, succeeded, newTransactionMessage, reviewCompleted = false }) {
    const { transactionId, tabId, locationId } = payload;

    if (!newTransactionMessage) {
        if (succeeded) yield call(closeTransactionPanelAndWait, { transactionId, tabId, locationId, reviewCompleted });
        return;
    }

    yield call(handleDeskAssignedMessage, newTransactionMessage);
    const newTransactionId = resolveTransactionId(newTransactionMessage);

    if (succeeded) yield call(closeTransactionPanelAndWait, { transactionId, tabId, locationId, reviewCompleted });

    const transactions = yield select(selectTransactions);
    const newTransaction = transactions.find((transaction) => transaction.id === newTransactionId);
    yield call(activateStartedTransaction, newTransaction);
}

// Unpinned lane with no peers left: assign the gate's video-wall camera to me. The new transaction is not
// returned here; it arrives later via the ordinary DeskAssignedMessage pipeline.
function* triggerAssignVwCameraToMe(gateCameraId) {
    if (!gateCameraId) {
        yield call(notificationsHandler, { title: 'No camera configured for this gate; could not start a new transaction.', variant: 'warning' });
        return false;
    }
    try {
        yield call(postCameraManual, gateCameraId, { assignToMe: true, missedDetection: false });
        return true;
    } catch (err) {
        yield call(notificationsHandler, { err, title: 'Error assigning a new transaction for this lane' });
        return false;
    }
}

// Shared orchestration for "submit/discard and start new". Always defers the close so removal + next-active
// selection are deterministic across submit/discard and tab/split view. Replacement creation runs in
// parallel with the submit/discard (a rare failure can leave an extra transaction open - accepted tradeoff).
export function* runStartNew({ payload, gateId, action }) {
    const { transactionId } = payload;
    const reviewCompleted = action === submitForm;

    yield put(formControlActions.setStartNewInProgress({ transactionId }));
    try {
        const laneTransactionCount = yield select(transactionSelectors.createTransactionsByGateIdCountSelector(gateId));
        const hasPeers = laneTransactionCount > 1;

        if (hasPeers) {
            const succeeded = yield call(action, { payload: { ...payload, deferClose: true } });
            if (succeeded) {
                yield call(closeTransactionPanelAndWait, { ...payload, reviewCompleted });
                yield call(activateFirstLaneTransaction, gateId);
            }
            return;
        }

        const isPinned = yield select(transactionSelectors.selectIsLanePinned(gateId));

        if (isPinned) {
            const [succeeded, newTransactionMessage] = yield all([
                call(action, { payload: { ...payload, deferClose: true } }),
                call(createLaneReplacementTransaction, gateId),
            ]);
            yield call(finalizeStartNew, { payload, succeeded, newTransactionMessage, reviewCompleted });
            return;
        }

        const closingTransaction = yield select(transactionSelectors.selectTranasctionById(transactionId));
        const gateCameraId = resolveVideoWallGateCameraId(closingTransaction?.camsConfig);
        const [succeeded] = yield all([call(action, { payload: { ...payload, deferClose: true } }), call(triggerAssignVwCameraToMe, gateCameraId)]);
        if (succeeded) yield call(closeTransactionPanelAndWait, { ...payload, reviewCompleted });
    } finally {
        yield put(formControlActions.setStartNewInProgress({ transactionId, inProgress: false }));
    }
}

export function* submitFormAndStartNew({ payload }) {
    const { transactionId, gateId, autoPopulateEmptyFormFields, blockSubmissionOverrides = null, pulseShortcutUsed = false } = payload;

    // Validate the post-population form; an invalid form falls back to plain submit to surface errors.
    let form = yield select(selectFormById(transactionId));
    if (autoPopulateEmptyFormFields) {
        form = (yield call(populateEmptyTransactionFormFields, form)) || form;
    }
    if (!payload.dynamicForm && (yield call(applyOcrAutofill, transactionId))) form = yield select(selectFormById(transactionId));
    if (getFirstInvalidField(form)) {
        yield call(submitForm, { payload });
        return;
    }

    // Earlier than submitForm's own check: runStartNew mints the replacement in parallel, so
    // blocking only there would still leave an extra transaction on the lane.
    const submitBlockers = yield call(resolveSubmitBlockers, {
        transactionId,
        pulseShortcutUsed,
        blockSubmissionOverrides,
        dynamicForm: payload.dynamicForm,
    });
    if (submitBlockers.length) {
        yield put(formControlActions.openSubmitBlockDialog({ transactionId, startNew: true, blockers: submitBlockers, payload }));
        console.log(`submitFormAndStartNew(): transactionId=${transactionId} submit blocked by ${submitBlockers.join(', ')}`);
        return;
    }

    yield call(runStartNew, { payload, gateId, action: submitForm });
}

export function* discardTransactionAndStartNew({ payload }) {
    const { gateId } = payload;
    yield call(runStartNew, { payload, gateId, action: discardTransaction });
}

export function* discardMultipleTransactions({ payload }) {
    const { transactions, discardReason } = payload;

    const dataToSend = [];
    for (const { id: transactionId, reopened } of transactions) {
        console.log(`discardTransaction(): transactionId=${transactionId} initiating discard`);
        const form = yield select(selectFormById(transactionId));
        const agentTime = form?.form?.values?.agentTime ?? form?.transactionData?.agentTime ?? null;
        dataToSend.push({ transactionId, reopened, agentTime });
        yield put(formControlActions.setdiscardTransactionInProgress({ transactionId }));
    }

    try {
        const response = yield axiosAiq.put('transactions/trucks/discard/list', { transactions: dataToSend, discardReason });
        const { code, defaultMessage } = response.data;

        // Handle SUCCESS or PARTIAL - both had same cleanup
        if (code === 'SUCCESS' || code === 'PARTIAL') {
            const locationIds = new Set();

            for (const transaction of transactions) {
                const { id: transactionId, tabId, locationId } = transaction;

                yield sendLocalStorageSignal('transactionClosed', transactionId);
                sharedWorkerActions.transactionFinished(transactionId);
                yield put(transactionActions.cleanTransactionPanelOnClose(tabId));
                yield call(closeTransactionPanel, { payload: { tabId, transactionId, locationId } });
                yield put(faceMatchActions.clearHistory({ transactionId }));

                locationIds.add(locationId);
            }
            const viewModeTag = yield call(getTransactionViewMode);
            console.log(
                `discardMultipleTransaction(): ${viewModeTag} transactionId=${dataToSend.map((item) => item.transactionId)} successfully discarded`
            );

            if (code === 'SUCCESS') {
                yield put(transactionActions.setLastClosedTransactionId(transactions.at(-1).id));
            }

            // Invalidate all cached data for expanded panel topics (appointments, history, manual)
            invalidateAppointmentCache();
            for (const locationId of locationIds) {
                yield put(tableControlActions.invalidateLocationTables(locationId));
            }
            // Refresh table data for each unique location
            for (const locationId of locationIds) {
                yield put(
                    tableControlActions.fetchTableDataStart({
                        tableId: locationId,
                        locationId,
                        pageNum: 0,
                    })
                );
            }

            if (code === 'PARTIAL') {
                const convertedMessage = convertText(defaultMessage);
                yield call(notificationsHandler, { title: convertedMessage });
            }
        } else if (code === 'NOTHING_DISCARDED') {
            const convertedMessage = parseErrorMessageFromResponse(defaultMessage);
            yield call(notificationsHandler, { title: convertedMessage });
        }
    } catch (err) {
        if (yield call(handleDeskInactive, err)) return;

        yield call(notificationsHandler, {
            err,
            title: 'Error discarding transactions',
        });
    }
    for (const { id: transactionId } of transactions) {
        yield put(formControlActions.setdiscardTransactionInProgress({ transactionId, inProgress: false }));
    }
}

export function* draftMail({ payload }) {
    const { transaction, emailType, additionalInfo, skipTransactionData } = payload;
    const { id: transactionId } = transaction;

    yield put(formControlActions.setDraftMailTransactionInProgress({ transactionId: transactionId }));

    try {
        console.log(`draftMail(): transactionId=${transactionId}, skipTransactionData=${skipTransactionData} initiating draft mail`);

        const req = {
            yardId: transaction.gate?.location?.name?.toLowerCase(),
            deskName: transaction.desk?.name,
            agentName: transaction.account?.username,
            transactionId: transactionId,
            transaction: !skipTransactionData ? transaction : null,
            emailType: emailType,
            additionalEmailInfo: additionalInfo,
        };

        const response = yield axiosAiq.post('api/gmail/draft', req);

        console.log(`draftMail(): transactionId=${transactionId} is successfully drafted`);
        yield put(formControlActions.setDraftMailTransactionInProgress({ transactionId, inProgress: false }));
        yield call(notificationsHandler, {
            title: response?.data?.message,
            variant: 'info',
        });
    } catch (err) {
        if (yield call(handleDeskInactive, err)) return;

        if (err.response?.status === 400) {
            const authUrl = err.response?.data?.authorizationUrl;
            if (authUrl) {
                window.open(authUrl, '_blank', 'width=600,height=700,noopener,noreferrer');
                yield call(notificationsHandler, {
                    title: 'Authentication required. Please authorize Gmail access in the popup window.',
                    variant: 'info',
                });
            } else {
                yield call(notificationsHandler, {
                    err,
                    title: 'Error drafting mail for transaction',
                });
            }

            yield put(formControlActions.setDraftMailTransactionInProgress({ transactionId, inProgress: false }));
            return;
        }

        if (err?.response?.status === 503) {
            yield call(notificationsHandler, {
                variant: 'info',
                title: 'AIQ not available right now, please try again later.',
            });
            yield put(formControlActions.setDraftMailTransactionInProgress({ transactionId, inProgress: false }));
            return;
        }

        console.log(`draftMail(): transactionId=${transactionId}, drafting mail error=${JSON.stringify(err)}`);
        yield call(notificationsHandler, {
            err,
            title: `Error drafting mail for transaction ${transactionId}`,
        });
        yield put(formControlActions.setDraftMailTransactionInProgress({ transactionId, inProgress: false }));
    }
}

// ... (rest of the code remains the same)
export function* returnToQueueTransactions({ payload }) {
    const { transactionId, gateId } = payload;
    yield put(formControlActions.setReturnToQueueIsInProgress(true));
    const allTransactions = yield select(selectTransactions);
    const transactionWithSameGateId = allTransactions?.filter((transaction) => transaction?.gateId === gateId);
    let filledTransactionToSubmit = [];

    try {
        for (const el of transactionWithSameGateId) {
            let form = yield select(selectFormById(el.id));
            let transactionToSubmit = yield call(prepareTransactionForSubmit, form);
            filledTransactionToSubmit.push(transactionToSubmit);
        }

        const config = {
            headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json, application/javascript, text/javascript, text/json',
            },
        };

        const url = 'transactions/trucks/return-to-queue';
        const data = yield axiosAiq.put(url, filledTransactionToSubmit, config);

        if (data.status === 200) {
            yield put(formControlActions.setReturnToQueueIsInProgress(false));
            for (const el of transactionWithSameGateId) {
                yield call(sendLocalStorageSignal, 'transactionClosed', el.id);
                yield call(sharedWorkerActions.transactionFinished, el.id);
                yield put(
                    tableControlActions.fetchTableDataStart({
                        tableId: el.id,
                        locationId: el.locationId,
                        pageNum: 0,
                    })
                );

                yield put(
                    transactionActions.closeTransactionPanel({
                        tabId: el.tabId,
                        transactionId: el.id,
                        locationId: el.locationId,
                        queueFinishedTransaction: false,
                        // Bulk finish / discard-multiple, per transaction, after success.
                        activityEndReason: ACTIVITY_END_REASON.COMPLETED_BY_SELF,
                    })
                );
                yield put(transactionActions.setLastClosedTransactionId(transactionId));
            }
        }
    } catch (err) {
        yield put(formControlActions.setReturnToQueueIsInProgress(false));
        if (err.response?.data?.error.includes('NO_SUITABLE_DESK_FOUND')) {
            yield put(formControlActions.setReturnToQueueError(getFormattedErrorMessage(err.response.data.error)));
        } else {
            yield put(formControlActions.setReturnToQueueError('Return to Queue failed. Please try again.'));
        }
    }
}

export function* refreshTransaction({ transactionId, form }) {
    try {
        const response = yield axiosAiq.get(`/transactions/trucks/${transactionId}`);

        const { data: transactionData } = response;
        const { values, errors } = form.form;

        yield put(formControlActions.setTransactionData({ transactionData }));
        Object.keys(values).forEach((fieldName) => {
            const value = values[fieldName];
            const newValue = readFromObj(transactionData, fieldName);
            if (newValue !== PROPERTY_NOT_FOUND && newValue !== value) {
                store.dispatch(
                    formControlActions.setFieldValue({
                        formId: transactionId,
                        fieldName,
                        value: newValue,
                    })
                );

                if (errors[fieldName] && newValue)
                    store.dispatch(
                        formControlActions.setFieldError({
                            formId: transactionId,
                            fieldName,
                            error: false,
                        })
                    );
            }
        });
    } catch (err) {
        yield put(formControlActions.setSubmitFormInProgress({ transactionId, inProgress: false }));
        yield call(notificationsHandler, {
            err,
            title: 'Error refreshing transaction',
        });
    }
}

export function* fetchTransactionFormConfig({ transactionId, initNewFormData, inventoryMode }) {
    perf.mark(transactionId, perf.markNames.fetchFormConfigStart);
    try {
        const { locationId = 0, gateId = 0, companyId = 0 } = initNewFormData;
        console.log(`fetchTransactionFormConfig(): Fetch transaction form config, transactionId=${transactionId}`);
        yield put(formControlActions.fetchTransactionFormConfigStart());

        yield call(startLocationLookupDataLoad, { locationId, inventoryMode });

        const formConfigResponse = yield axiosAiq(`/transactions/trucks/form/fields`, {
            params: { gateId, locationId, companyId },
        });

        console.log(`fetchTransactionFormConfig(): Form config received, transactionId=${transactionId}`);
        const { data: transactionFormConfig } = formConfigResponse;

        // A value the location has since deactivated is absent from the lookup the backend sends, so
        // re-add it here or the field renders empty on an older transaction. initializeNewForm stores
        // transactionData before calling this, so the form is already in the slice.
        const form = yield select(selectFormById(transactionId));
        transactionFormConfig.fields = preserveRetiredLookupValues(transactionFormConfig.fields, form?.transactionData);

        const isSplitViewMode = yield select(transactionSelectors.selectTransactionsSplitViewMode);

        yield put(
            formControlActions.fetchTransactionFormConfigSuccess({
                transactionId,
                transactionFormConfig,
                isSplitViewMode,
            })
        );

        // If this transaction was discarded earlier and is now being reopened,
        // rehydrate the operator's previously entered values (server-side discard
        // does not persist form values). Safe no-op when there's no cached snapshot.
        yield call(restoreDiscardedFormValues, transactionId);
    } catch (err) {
        yield call(notificationsHandler, {
            err,
            action: formControlActions.fetchTransactionFormConfigFailed,
            title: 'Error fetching transaction form config file',
        });
    }
    perf.mark(transactionId, perf.markNames.fetchFormConfigEnd);
}

export function* validateTransactionWithInventoryData({ payload: { formId } }) {
    if (!formId) return;
    try {
        const featureFlagConfig = yield select(selectFeatureFlagData);
        const { inventoryEnabled = false } = featureFlagConfig;
        if (!inventoryEnabled) return;
        const form = yield select(selectFormById(formId));
        // The backend answers "no configuration" for any other mode.
        if (form?.transactionData?.gate?.location?.inventoryMode !== 'ON') return;
        const isSplitViewMode = yield select(transactionSelectors.selectTransactionsSplitViewMode);
        if (isSplitViewMode) {
            const { forms } = yield select(selectFormsSlice);
            yield call(markInventoryValidationSkipped, formId, forms);
            return;
        }

        const transactionToSubmit = yield call(prepareTransactionForSubmit, form);
        const updatedTransaction = populateInventoryDataAutomation(transactionToSubmit, form);
        const signature = getInventoryValidationSignature(updatedTransaction);

        yield put(formControlActions.fetchInventoryMappingStart({ formId, signature }));
        const inventoryMapping = yield call(inventoryValidation, updatedTransaction);
        yield put(
            formControlActions.setInventoryDataMapping({
                inventoryMapping,
                formId,
                signature,
            })
        );
    } catch {
        yield put(formControlActions.fetchInventoryMappingFailed(formId));
    }
}

export function* validateTransactionFormField({ payload: { formId, fieldName } }) {
    try {
        let form = yield select(selectFormById(formId));
        const submittedValue = form?.form?.values?.[fieldName];

        const triggeringTransaction = yield select(transactionSelectors.selectTranasctionById(formId));
        const triggeringGateId = triggeringTransaction?.gateId;
        if (triggeringGateId) {
            yield put(transactionActions.setSheetAnnouncementPendingGate({ gateId: triggeringGateId, transactionId: formId }));
        }

        const transactionToSubmit = yield call(prepareTransactionForSubmit, form);

        const response = yield axiosAiq.put(`/transactions/trucks/${formId}/validate?dataField=${fieldName}`, transactionToSubmit);
        const { data: transactionData } = response;

        form = yield select(selectFormById(formId));

        if (!form) {
            console.log(
                `validateTransactionFormField(): transactionId=${formId}, fieldName=${fieldName} validation successful,but transaction already finished, exiting...`
            );
            return;
        }

        yield put(formControlActions.setTransactionData({ transactionData }));

        yield put(
            formControlActions.applyLookupBlockingValidation({
                transactionId: formId,
                fieldName,
                validationResult: transactionData?.validationResult || null,
            })
        );

        const {
            form: { values },
        } = form;

        const editedSinceSubmit = values[fieldName] !== submittedValue;
        let newValue = readFromObj(transactionData, fieldName);
        if (!editedSinceSubmit && newValue !== values[fieldName]) {
            store.dispatch(
                formControlActions.setFieldValue({
                    formId,
                    fieldName,
                    value: newValue,
                })
            );
        }
    } catch (err) {
        yield call(notificationsHandler, {
            err,
            title: `Error validating ${fieldName}`,
        });
    }
}

let ongoingRelayRequests = {};

export function* activateRelay({ payload: { relaySlotId, operation } }) {
    const requestId = `${relaySlotId}-${operation}`;
    try {
        if (ongoingRelayRequests[requestId]) return;
        else ongoingRelayRequests[requestId] = true;
        console.log(`activateRelay(): ACTIVATING operation=${operation} on relayId=${relaySlotId}`);
        yield axiosAiq.post(`/relay/submit/${relaySlotId}?operation=${operation}`);
        console.log(`activateRelay(): SUCCESSFUL operation=${operation} on relayId=${relaySlotId}`);

        let title = `Gate successfully `;
        switch (operation) {
            case 'ON':
                title += 'opened';
                break;
            case 'OFF':
                title += 'closed';
                break;
            default:
                title += 'activated';
        }
        yield call(notificationsHandler, {
            title,
            variant: 'success',
        });
    } catch (err) {
        console.log(`activateRelay(): ERROR activating operation=${operation} on relayId=${relaySlotId}`);
        const title = `Error activating operation=${operation} on relayId=${relaySlotId} `;

        yield call(notificationsHandler, {
            err,
            title,
        });
    }
    // eslint-disable-next-line no-unused-vars
    const { [requestId]: toBeDeleted, ...rest } = ongoingRelayRequests;
    ongoingRelayRequests = rest;
}

export function* onActivateRelay() {
    yield takeEvery(formControlActions.activateRelay.type, activateRelay);
}

export function* onDiscardTransactionStart() {
    yield takeEvery(formControlActions.discardTransactionStart.type, discardTransaction);
}

export function* onDiscardTransactionAndStartNewStart() {
    yield takeEvery(formControlActions.discardTransactionAndStartNewStart.type, discardTransactionAndStartNew);
}

export function* onDraftMailStart() {
    yield takeEvery(formControlActions.draftMailStart.type, draftMail);
}

export function* onDiscardMultipleTransactionsTransactionsStart() {
    yield takeEvery(formControlActions.discardMultipleTransactionsStart.type, discardMultipleTransactions);
}

export function* onSubmitFormStart() {
    yield takeLeading(formControlActions.submitFormStart.type, submitForm);
}

export function* onSubmitFormAndStartNewStart() {
    yield takeLeading(formControlActions.submitFormAndStartNewStart.type, submitFormAndStartNew);
}

export function* onOverrideSubmitBlockStart() {
    yield takeLeading(formControlActions.overrideSubmitBlockStart.type, overrideSubmitBlock);
}

export function* onReturnToQueueTransactionsStart() {
    yield takeEvery(formControlActions.returnToQueueTransactionsStart.type, returnToQueueTransactions);
}

// Only validateTransactionFormField needs per-formId FIFO serialization: its response writes
// into form.values[fieldName] (formControlActions.setFieldValue), so an out-of-order response
// on a slow/throttled connection can stomp a fresher edit. validateTransactionWithInventoryData
// only ever writes to state.inventoryMapping[formId], so rather than sharing (and being
// head-of-line-blocked by) this queue it keeps just the latest request per form - see
// onValidateTransactionWithInventoryData.
function* processFormFieldValidationQueue(formId, queuesByFormId) {
    try {
        while (queuesByFormId[formId].length) {
            const action = queuesByFormId[formId].shift();
            yield call(validateTransactionFormField, action);
        }
    } finally {
        delete queuesByFormId[formId];
    }
}

export function* onValidateTransactionFormFieldQueue() {
    const channel = yield actionChannel(formControlActions.validateTransactionFormField.type, buffers.expanding());
    const queuesByFormId = {}; // formId -> queued actions waiting to run, deleted once drained
    while (true) {
        const action = yield take(channel);
        const { formId } = action.payload;
        if (!formId) {
            yield call(notificationsHandler, {
                err: new Error('validateTransactionFormField dispatched without a formId'),
                title: 'Error processing transaction validation queue',
            });
            continue;
        }
        const isNewQueue = !queuesByFormId[formId];
        if (isNewQueue) queuesByFormId[formId] = [];
        queuesByFormId[formId].push(action);
        if (isNewQueue) {
            // spawn (not fork): isolates this transaction's worker so an unexpected error here
            // cannot cascade up and cancel the sibling watchers in formControlSaga's yield all([...]).
            yield spawn(processFormFieldValidationQueue, formId, queuesByFormId);
        }
    }
}

// Latest per form: each blur sends the whole transaction, and a slower response to an older
// snapshot must not overwrite the indicator a later blur already set.
export function* onValidateTransactionWithInventoryData() {
    const tasksByFormId = new Map();
    while (true) {
        const action = yield take(formControlActions.validateTransactionWithInventoryData.type);
        const formId = action.payload?.formId;

        const previousTask = tasksByFormId.get(formId);
        if (previousTask?.isRunning()) yield cancel(previousTask);
        for (const [id, task] of tasksByFormId) {
            if (!task.isRunning()) tasksByFormId.delete(id);
        }

        tasksByFormId.set(formId, yield fork(validateTransactionWithInventoryData, action));
    }
}

export function* formControlSaga() {
    yield all([
        call(onSubmitFormStart),
        call(onSubmitFormAndStartNewStart),
        call(onOverrideSubmitBlockStart),
        call(onValidateTransactionFormFieldQueue),
        call(onValidateTransactionWithInventoryData),
        call(onDiscardTransactionStart),
        call(onDiscardTransactionAndStartNewStart),
        call(onDraftMailStart),
        call(onActivateRelay),
        call(onReturnToQueueTransactionsStart),
        call(onDiscardMultipleTransactionsTransactionsStart),
        call(onInitializeEditTransactionForm),
        call(onEditFormStart),
        call(onRemoveTransaction),
        ...copyToForm.init(),
    ]);
}

// --- Discarded-form snapshot / restore (FE-only rehydrate on reopen) ---

// Build a snapshot of the operator's manually-edited editable fields and persist
// it, so it can be restored if this discarded transaction is later reopened.
export function snapshotDiscardedFormValues(transactionId, form) {
    try {
        const values = form?.form?.values;
        const userEdited = form?.form?.userEdited;
        const fieldValueSources = form?.form?.fieldValueSources || {};
        const fields = form?.formConfig?.fields;

        if (!values || !userEdited || !fields) {
            clearDiscardedForm(transactionId);
            return;
        }

        // Only persist EDITABLE fields the operator actually edited. This avoids
        // clobbering server-truth read-only fields (gate/location/times) and
        // avoids re-introducing stale automation suggestions on reopen.
        const editableFieldNames = new Set(fields.filter((field) => field?.editable).map((field) => field?.dataField));

        const snapshotValues = {};
        const snapshotSources = {};
        const snapshotUserEdited = {};
        Object.keys(userEdited).forEach((fieldName) => {
            if (!userEdited[fieldName]) return;
            if (!editableFieldNames.has(fieldName)) return;
            const value = values[fieldName];
            if (value === undefined) return;
            snapshotValues[fieldName] = value;
            snapshotUserEdited[fieldName] = true;
            if (fieldValueSources[fieldName] != null) snapshotSources[fieldName] = fieldValueSources[fieldName];
        });

        if (!Object.keys(snapshotValues).length) {
            // Nothing meaningful to restore — make sure we don't leave a stale entry.
            clearDiscardedForm(transactionId);
            return;
        }

        // Keep userEdited in lock-step with the persisted values so the snapshot is
        // internally consistent (no dangling userEdited keys for fields we dropped).
        saveDiscardedForm(transactionId, {
            values: snapshotValues,
            userEdited: snapshotUserEdited,
            fieldValueSources: snapshotSources,
        });
    } catch (err) {
        console.warn(`snapshotDiscardedFormValues(): failed to snapshot form for transactionId=${transactionId}`, err);
    }
}

// Restore a previously discarded transaction's edited values onto the freshly
// initialized form. Clears the cache entry after a successful restore.
export function* restoreDiscardedFormValues(transactionId) {
    const snapshot = getDiscardedForm(transactionId);
    if (!snapshot) return;

    try {
        const form = yield select(selectFormById(transactionId));
        if (!form?.form) return;

        // Restore against the fields defined by the form config. Field components
        // register slightly later on mount, but registerNewFormField no longer
        // clobbers an already-populated (restored) value, so writing here first is
        // safe regardless of mount timing.
        const configFieldNames = new Set((form.formConfig?.fields || []).map((field) => field?.dataField));
        const { values = {}, userEdited = {}, fieldValueSources = {} } = snapshot;

        for (const fieldName of Object.keys(values)) {
            // Only restore fields that still exist on this transaction's form config.
            if (configFieldNames.size && !configFieldNames.has(fieldName)) continue;
            const value = values[fieldName];

            // Consume the persisted userEdited flag directly instead of re-deriving it.
            // Snapshots only persist user-edited editable fields, so this is normally
            // true; guard anyway so a stale/partial snapshot restores as a plain value.
            const wasUserEdited = userEdited[fieldName] === true;
            const source = fieldValueSources[fieldName];

            // `isRestore` is inert for the reducers. It exists for the activity-tracking seam
            // (BEAST-13036): rehydrating a reopened transaction re-applies values that were already
            // applied once, so it must not emit a second `field.value.applied` for each of them.
            //
            // Only the two VALUE writes below are actually watched, so only their flag does work
            // today. The two `setFieldValueSource` ones are kept for symmetry and as cover if that
            // action is ever added back to `directValueWriteActions()` - which is exactly the shape
            // of change that would otherwise reintroduce a restore burst silently.
            if (wasUserEdited) {
                // Combined action: one dispatch sets value + userEdited + source='user'
                // (replaces the old setFieldValue -> markFieldUserEdited pair).
                yield put(formControlActions.setFieldValueAndMarkEdited({ formId: transactionId, fieldName, value, isRestore: true }));
                // Only override the source when the snapshot recorded a non-'user'
                // one (setFieldValueAndMarkEdited already stamps 'user').
                if (source && source !== 'user') {
                    yield put(formControlActions.setFieldValueSource({ formId: transactionId, fieldName, source, isRestore: true }));
                }
            } else {
                yield put(formControlActions.setFieldValue({ formId: transactionId, fieldName, value, isRestore: true }));
                if (source) {
                    yield put(formControlActions.setFieldValueSource({ formId: transactionId, fieldName, source, isRestore: true }));
                }
            }

            // A restored value satisfies required validation for that field.
            // (setFieldValueAndMarkEdited intentionally does not touch errors, so
            // clear it here for both branches when there is a concrete value.)
            if (value != null && value !== '') {
                yield put(formControlActions.setFieldError({ formId: transactionId, fieldName, error: false }));
            }
        }

        console.log(`restoreDiscardedFormValues(): rehydrated discarded values for reopened transactionId=${transactionId}`);
    } catch (err) {
        console.warn(`restoreDiscardedFormValues(): failed to restore form for transactionId=${transactionId}`, err);
    } finally {
        clearDiscardedForm(transactionId);
    }
}

function* handleDeskInactive(err) {
    if (err?.response?.data?.error?.includes('DESK_INACTIVE')) {
        yield call(notificationsHandler, {
            title: 'Unsubscribed from desk due network problems, try to select desk again...',
            variant: 'info',
        });
        yield call(leaveDesk);
        return true;
    }
}
